import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { ITooltipActions } from '@gem/ui-kit';

import Tasks from 'APP/Tasks';
import { ChatMessage } from 'APP/store/Messages/Message/ChatMessage/ChatMessage';
import { User } from 'APP/store/Users/User/User';
import Entities from 'STORE';

const LIMIT = 10;

export interface IMessageViewsPopoverProps {
  message: ChatMessage;
  tooltip: ITooltipActions;
  onOpenReactionPopup(): void;
}

interface IMessageViewsPopoverPresenter {
  message: ChatMessage;
  isLoading: boolean;
  users: User[];
  isOpened: boolean;
  moreCount: number | null;
  setMessage(message: ChatMessage): void;
  loadViews(): Promise<void>;
  onMoreClick(): void;
  reset(): void;
}

export const useMessageViewsPopoverPresenter = ({
  message,
  tooltip,
  onOpenReactionPopup,
}: IMessageViewsPopoverProps): IMessageViewsPopoverPresenter => {
  const presenter = useLocalObservable<IMessageViewsPopoverPresenter>(() => ({
    message,
    isLoading: false,
    users: [],

    get isOpened(): boolean {
      return !presenter.isLoading && presenter.users.length > 0;
    },

    get moreCount(): number | null {
      if (presenter.message.views > LIMIT) {
        return presenter.message.views - presenter.users.length;
      }

      return null;
    },

    setMessage(message: ChatMessage): void {
      presenter.message = message;
    },

    async loadViews(): Promise<void> {
      presenter.isLoading = true;

      const { userIds } = await Tasks.messaging.getUsersViewedMessage({
        groupId: presenter.message.groupId,
        messageId: presenter.message.id,
        limit: LIMIT,
      });

      if (userIds.length > 0) {
        await Tasks.relationships.loadUnknownUsersByIds({ usersIds: userIds });
      }

      presenter.users = userIds
        .map((userId) => {
          return Entities.usersStore.getUserById(userId);
        })
        .filter((x) => !!x);
      presenter.isLoading = false;

      tooltip.update();
    },

    onMoreClick(): void {
      onOpenReactionPopup();
      tooltip.hide();
    },

    reset(): void {
      presenter.isLoading = false;
      presenter.users = [];
    },
  }));

  useEffect(() => {
    presenter.setMessage(message);
    presenter.loadViews();

    return () => {
      presenter.reset();
    };
  }, [message]);

  return presenter;
};
